"use client";

import { useEffect, useState } from "react";
import { api, type PlanOut } from "@/lib/api";
import { fallbackPrice, planPrices } from "@/lib/planPricing";
import { SafeBoundary } from "@/components/SafeBoundary";

/**
 * Rankbist's monthly prices next to three well-known tools, level by level.
 *
 * Our numbers come live from the plans on sale. The other three are each
 * company's public list price for monthly billing, in US dollars, read from
 * its own pricing page on the date in CHECKED. They change: re-check them
 * and update CHECKED whenever this is edited, because a stale number here
 * is a false statement about another company.
 */
const CHECKED = "October 2026";

const TIERS: {
  slug: string;
  label: string;
  others: { name: string; price: number }[];
}[] = [
  {
    slug: "starter",
    label: "Entry plans",
    others: [
      { name: "Frase", price: 49 },
      { name: "Surfer", price: 59 },
      { name: "Scalenut", price: 59 },
    ],
  },
  {
    slug: "pro",
    label: "Middle plans",
    others: [
      { name: "Scalenut", price: 89 },
      { name: "Surfer", price: 119 },
      { name: "Frase", price: 129 },
    ],
  },
  {
    slug: "advanced",
    label: "Top plans",
    others: [
      { name: "Scalenut", price: 199 },
      { name: "Surfer", price: 218 },
      { name: "Frase", price: 299 },
    ],
  },
];

/** Every section is 1400px wide now; `wide` and `narrow` are kept so existing callers still compile. */
function PriceComparisonUnsafe({
  wide: _wide = false,
  narrow: _narrow = false,
}: {
  wide?: boolean;
  narrow?: boolean;
}) {
  const [plans, setPlans] = useState<PlanOut[] | null>(null);
  useEffect(() => {
    api
      .getPlans()
      .then((p) => setPlans(p ?? []))
      .catch(() => setPlans([]));
  }, []);
  if (!plans) return null;

  const rows = TIERS.map((t) => {
    const plan = plans.find((p) => p.slug === t.slug);
    const monthly = plan
      ? planPrices(plan).find(
          (x) =>
            x.billing_interval === "MONTHLY" &&
            (!x.currency || x.currency === "USD"),
        )
      : undefined;
    if (!plan || !monthly) return null;
    const ours = monthly.price_cents / 100;
    const average =
      (t.others ?? []).reduce((sum, o) => sum + o.price, 0) / t.others.length;
    // Only claim "less" where it is true for every tool shown.
    if ((t.others ?? []).some((o) => o.price <= ours)) return null;
    return {
      ...t,
      plan,
      ours,
      oursText: fallbackPrice(monthly.price_cents, monthly.currency),
      less: Math.round((1 - ours / average) * 100),
      top: Math.max(...(t.others ?? []).map((o) => o.price)),
    };
  }).filter((r): r is NonNullable<typeof r> => r !== null);
  if (rows.length === 0) return null;

  return (
    <section className="px-3 pb-7 sm:px-6 sm:pb-12" aria-labelledby="compare-prices-title">
      <div
        className={`dark-card mx-auto max-w-[1400px] rounded-[24px] p-5 sm:rounded-[32px] sm:p-12`}
      >
        <div
          className="pointer-events-none absolute -left-28 -top-32 h-[320px] w-[320px] rounded-full bg-violet opacity-25"
          style={{ filter: "blur(80px)" }}
        />
        <div className="relative mx-auto max-w-[720px] text-center">
          <h2
            id="compare-prices-title"
            className="text-[1.3rem] font-extrabold leading-tight tracking-[-0.02em] sm:text-[2.4rem]"
          >
            The same job, for about half the price
          </h2>
          <p className="mt-3 text-[0.9rem] leading-relaxed text-white/65 sm:text-[1rem]">
            What you pay each month for Rankbist, next to three tools you
            probably already know.
          </p>
        </div>

        <div className="relative mt-6 grid grid-cols-1 gap-3 sm:mt-10 sm:gap-6 lg:grid-cols-3">
          {rows.map((r) => (
            <div
              key={r.slug}
              className="rounded-[20px] bg-white/[0.06] p-4 ring-1 ring-white/10 sm:rounded-[24px] sm:p-6"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-[0.86rem] font-bold uppercase tracking-[0.08em] text-white/55">
                  {r.label}
                </h3>
                <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-[0.78rem] font-extrabold text-emerald-200">
                  {r.less}% less on average
                </span>
              </div>
              <ul className="mt-5 space-y-3.5">
                <li>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[0.98rem] font-extrabold">
                      Rankbist {r.plan.plan_name}
                    </span>
                    <span className="tabular-nums text-[1.5rem] font-extrabold leading-none">
                      {r.oursText}
                      <small className="text-[0.76rem] font-semibold text-white/55">
                        /mo
                      </small>
                    </span>
                  </div>
                  <div className="mt-2 h-2.5 rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#A488FF] via-violet to-pink"
                      style={{
                        width: `${Math.max((r.ours / r.top) * 100, 6)}%`,
                      }}
                    />
                  </div>
                </li>
                {(r.others ?? []).map((o) => (
                  <li key={o.name}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[0.92rem] font-semibold text-white/70">
                        {o.name}
                      </span>
                      <span className="tabular-nums text-[1.05rem] font-bold text-white/70">
                        ${o.price}
                        <small className="text-[0.74rem] font-semibold text-white/40">
                          /mo
                        </small>
                      </span>
                    </div>
                    <div className="mt-2 h-2.5 rounded-full bg-white/10">
                      <div
                        className="h-full rounded-full bg-white/30"
                        style={{ width: `${(o.price / r.top) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="relative mx-auto mt-8 max-w-[1400px] text-center text-[0.78rem] leading-relaxed text-white/45">
          Monthly prices when billed monthly, in US dollars, taken from each
          company&apos;s public pricing page in {CHECKED}. Plans differ in what
          they include, so compare features as well as price. Product names
          belong to their owners.
        </p>
      </div>
    </section>
  );
}

/** PriceComparison, kept on its own: if it ever fails to render, only this part shows a note. */
export function PriceComparison(props: Parameters<typeof PriceComparisonUnsafe>[0]) {
  return (
    <SafeBoundary name="PriceComparison">
      <PriceComparisonUnsafe {...props} />
    </SafeBoundary>
  );
}
